import React, { useState } from "react";
import { Autocomplete, Chip, TextField } from "@mui/material";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Props {
  value: string[];
  onChange: (emails: string[]) => void;
  max?: number;
  placeholder?: string;
  sx?: object;
}

/**
 * A list of email addresses as removable chips. Type an address and press
 * Enter, comma or Tab (or leave the field); invalid or duplicate entries are
 * rejected with a message rather than silently dropped.
 */
const EmailChipsInput = ({ value, onChange, max = 20, placeholder = "Type an email and press Enter", sx }: Props) => {
  const [input, setInput] = useState("");
  const [error, setError] = useState("");

  const add = (raw: string) => {
    const candidates = raw.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean);
    if (candidates.length === 0) return;

    const next = [...value];
    for (const email of candidates) {
      if (!EMAIL_RE.test(email)) { setError(`"${email}" is not a valid email address.`); return; }
      if (next.includes(email)) continue;
      if (next.length >= max) { setError(`Up to ${max} addresses.`); return; }
      next.push(email);
    }
    setError("");
    setInput("");
    onChange(next);
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      options={[]}
      value={value}
      inputValue={input}
      onInputChange={(_, v, reason) => {
        if (reason === "reset") return;
        setError("");
        // A pasted "a@x.com, b@x.com" or a typed trailing comma adds at once.
        if (/[,;]/.test(v)) add(v); else setInput(v);
      }}
      onChange={(_, v, reason) => {
        if (reason === "createOption") add(String(v[v.length - 1] ?? ""));
        else onChange((v as string[]).map((e) => e.toLowerCase()));
      }}
      renderTags={(tags, getTagProps) =>
        tags.map((email, index) => (
          <Chip size="small" label={email} {...getTagProps({ index })} key={email} />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={value.length ? "" : placeholder}
          error={Boolean(error)}
          helperText={error || "Press Enter or comma to add. Up to " + max + " addresses."}
          onBlur={() => add(input)}
          onKeyDown={(e) => {
            if (e.key === "Tab" && input.trim()) { e.preventDefault(); add(input); }
          }}
          sx={sx}
        />
      )}
    />
  );
};

export default EmailChipsInput;
